import { type ForwardRefExoticComponent, forwardRef } from 'react';

import { AlertAction } from './alert-action';
import { AlertBody } from './alert-body';
import { AlertClose } from './alert-close';
import { AlertRoot } from './alert-root';
import type { AlertPropsInterface } from './types/alert.types';

/**
 * Alerts are system initiated status messages that are embedded within the content of a page.
 * Alerts should be used to notify users with status or information related to the surrounding content.
 *
 * @since 7.21.0
 */
export const Alert = forwardRef<HTMLDivElement, AlertPropsInterface>(
  (props: AlertPropsInterface, forwardedRef) => {
    const { actionButton, children, closeButton, ...rest } = props;

    return (
      <Alert.Root ref={forwardedRef} {...rest}>
        <Alert.Body>
          {children}
          {actionButton ? <Alert.Action>{actionButton}</Alert.Action> : null}
        </Alert.Body>
        {closeButton ? <Alert.Close>{closeButton}</Alert.Close> : null}
      </Alert.Root>
    );
  },
) as ForwardRefExoticComponent<AlertPropsInterface> & {
  Root: typeof AlertRoot;
  Action: typeof AlertAction;
  Body: typeof AlertBody;
  Close: typeof AlertClose;
};

Alert.Root = AlertRoot;
Alert.Action = AlertAction;
Alert.Body = AlertBody;
Alert.Close = AlertClose;

Alert.displayName = 'Alert';
